import React, { useEffect, useRef, useState, useMemo } from 'react';
import { createBrowserRouter, RouterProvider, Navigate, Outlet, useParams, useBlocker } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';

// App sections
import Builder      from './components/Builder/Builder';
import FloorPlan    from './components/FloorPlan/FloorPlan';
import RoomEditor   from './components/RoomEditor/RoomEditor';
import House3DView  from './components/House3DView/House3DView';
import useScrollDirection from './hooks/useScrollDirection';
import Header       from './components/Header/Header';
import Landing      from './components/Landing/Landing';

// Auth
import { AuthProvider }   from './context/AuthContext';
import ProtectedRoute     from './components/ProtectedRoute';
import AdminRoute         from './components/AdminRoute';
import AdminDashboard     from './pages/AdminDashboard';
import Login              from './pages/Login';
import Register           from './pages/Register';
import { About, Privacy } from './pages/InfoPages';
import NotFound from './pages/NotFound';
import Plans from './pages/Plans';
import ForgotPassword     from './pages/ForgotPassword';
import Dashboard          from './pages/Dashboard';
import Settings           from './pages/Settings';
import Profile            from './pages/Profile';
import Workspace          from './pages/Workspace';
import SharedProject      from './pages/SharedProject';
import SearchPage         from './pages/SearchPage';
import DesignerProfile    from './pages/DesignerProfile';
import ProductPage        from './pages/ProductPage';
import { USER_TYPES }     from './constants/designer';
import MyDesigners        from './pages/MyDesigners';
import MyLiked            from './pages/MyLiked';
import Basket             from './pages/Basket';

// Firebase + Redux
import { getProject, saveProjectToWeb } from './firebase/workspaceService';
import { loadDesign, goToPhase, selectRoom, gridSizeCodeToCells, GRID_SIZES, DEFAULT_GRID_SIZE_CODE } from './store/houseSlice';
import { store }      from './store';

// Unsaved changes context
import { UnsavedChangesCtx } from './context/UnsavedChangesContext';
import { GuestModeProvider } from './context/GuestModeContext';
import { ThemeProvider } from './context/ThemeContext';
import { ChatUIProvider } from './context/ChatUIContext';
import { NotificationProvider } from './context/NotificationContext';
import { BasketProvider } from './context/BasketContext';
import MessageToasts from './components/MessageToasts';
import NotificationPrimer from './components/NotificationPrimer';
import { PullToRefreshHost } from './components/PullToRefresh';
import { RefreshProvider } from './context/RefreshContext';
import MobileTabBar from './components/MobileTabBar';
import AccountType        from './pages/AccountType';
import { loadGuestDesign, clearGuestDesign } from './utils/guestDesign';
import GuestWelcomeModal from './components/GuestWelcomeModal';
import SupportWidget from './components/SupportWidget';

// i18n
import { LangProvider, useT } from './i18n';
import './App.css';

const queryClient = new QueryClient();

const BLANK_DESIGN = {
  version: 1, phase: 'build',
  rooms: [], designs: {}, furniture: [], lighting: [],
  wallSegments: [], autoWallOpenings: {}, wallHeight: 2.8,
  nextId: 1, nextFurnitureId: 1, nextLightingId: 1, nextOpeningId: 1,
  nextWallId: 1, nextWallOpeningId: 1,
};

const FILE_VERSION = 1;

function pickSaveable(s) {
  return {
    rooms:            s.rooms,
    designs:          s.designs,
    furniture:        s.furniture,
    lighting:         s.lighting,
    wallSegments:     s.wallSegments,
    freeWalls:        s.freeWalls,
    nextFreeWallId:   s.nextFreeWallId,
    freeRooms:        s.freeRooms,
    nextFreeRoomId:   s.nextFreeRoomId,
    autoWallOpenings: s.autoWallOpenings,
    wallHeight:       s.wallHeight,
  };
}

function UnsavedModal({ onSave, onLeave, onStay, saving }) {
  const { t } = useT();
  return (
    <div className="unsaved-overlay">
      <div className="unsaved-modal">
        <div className="unsaved-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
            <path d="M12 9v4M12 17h.01" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round"/>
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
              stroke="#f59e0b" strokeWidth="2" strokeLinejoin="round"/>
          </svg>
        </div>
        <h3 className="unsaved-title">{t('unsaved_title')}</h3>
        <p className="unsaved-sub">{t('unsaved_sub')}</p>
        <div className="unsaved-actions">
          <button className="unsaved-btn unsaved-btn--save" onClick={onSave} disabled={saving}>
            {saving ? t('saving') : t('unsaved_save')}
          </button>
          <button className="unsaved-btn unsaved-btn--leave" onClick={onLeave} disabled={saving}>{t('unsaved_leave')}</button>
          <button className="unsaved-btn unsaved-btn--stay"  onClick={onStay}>{t('unsaved_stay')}</button>
        </div>
      </div>
    </div>
  );
}

function AppInner() {
  const { projectId } = useParams();
  const dispatch      = useDispatch();
  const houseState    = useSelector(s => s.house);
  const loadedRef     = useRef(false);
  const savedSnapshotRef = useRef(null);
  const pendingActionRef = useRef(null);
  const [isDirty,        setIsDirty]        = useState(false);
  const [showModal,      setShowModal]      = useState(false);
  const [isSavingLeave,  setIsSavingLeave]  = useState(false);

  // ── React Query fetch ──────────────────────────────────────────────────────
  // staleTime: 0  → data is immediately stale; refetched on every mount
  // gcTime: 0     → cache discarded when component unmounts (no cross-session reuse)
  // refetchOnWindowFocus/Reconnect: false → without this, switching tabs (or
  // apps) and coming back re-triggers the fetch below, which re-dispatches
  // loadDesign and forces the phase back to 'build' — kicking the user out of
  // whatever 3D/plan view they were in. Autosave already keeps Firestore in
  // sync from this tab, so there's nothing to reconcile by refetching here.
  const { data: fetchedData, isLoading, isError } = useQuery({
    queryKey:  ['project', projectId],
    queryFn:   () => getProject(projectId),
    enabled:   !!projectId,
    staleTime: 0,
    gcTime:    0,
    retry:     1,
    refetchOnWindowFocus: false,
    refetchOnReconnect:   false,
  });

  // Load into Redux once per projectId
  useEffect(() => {
    if (!fetchedData) return;
    loadedRef.current = false;
    const raw    = fetchedData?.data;
    const parsed = raw && raw !== '{}' && typeof raw === 'string' ? JSON.parse(raw) : null;
    const design = parsed?.version === 1 ? parsed : BLANK_DESIGN;
    // gridSize isn't part of the saved design JSON — it's a sibling Firestore
    // field ('S'/'M'/'L') chosen once when the project was created.
    dispatch(loadDesign({ ...design, phase: 'build', gridSize: gridSizeCodeToCells(fetchedData?.size) }));
  }, [fetchedData, dispatch]);

  // After loadDesign settles, snapshot the state and start dirty tracking.
  // Also restore the phase/room the URL points at, so an actual page reload
  // (as opposed to a tab-switch, which no longer refetches at all — see
  // above) lands back on the same 3D/plan view instead of the Builder.
  useEffect(() => {
    if (loadedRef.current) return;
    if (!fetchedData) return;
    // One tick after load so the Redux dispatch has been applied
    const id = setTimeout(() => {
      const params  = new URLSearchParams(window.location.search);
      const urlPhase = params.get('page');
      const urlRoom  = params.get('room');
      const s = store.getState().house;
      if (urlPhase === 'design' && urlRoom && s.rooms.some(r => String(r.id) === urlRoom)) {
        dispatch(selectRoom(Number(urlRoom)));
      } else if ((urlPhase === 'plan' || urlPhase === 'view3d') && s.rooms.length > 0) {
        dispatch(goToPhase(urlPhase));
      }
      savedSnapshotRef.current = JSON.stringify(pickSaveable(store.getState().house));
      setIsDirty(false);
      loadedRef.current = true;
    }, 50);
    return () => clearTimeout(id);
  }, [fetchedData]); // eslint-disable-line

  // Keep the URL's `page` (and `room`, for the room editor) in sync with the
  // current phase, so a reload restores the same view. Uses the raw History
  // API (not react-router's navigate/setSearchParams) so this bookkeeping
  // never runs through useBlocker — otherwise every phase change while
  // isDirty is true would pop the "unsaved changes" leave-confirmation.
  useEffect(() => {
    if (!loadedRef.current) return; // don't clobber the URL before restoring from it
    const url = new URL(window.location.href);
    if (houseState.phase === 'design' && houseState.selectedRoomId != null) {
      url.searchParams.set('page', 'design');
      url.searchParams.set('room', String(houseState.selectedRoomId));
    } else if (houseState.phase === 'plan' || houseState.phase === 'view3d') {
      url.searchParams.set('page', houseState.phase);
      url.searchParams.delete('room');
    } else {
      url.searchParams.delete('page');
      url.searchParams.delete('room');
    }
    const newHref = url.pathname + url.search + url.hash;
    if (newHref !== window.location.pathname + window.location.search + window.location.hash) {
      window.history.replaceState(window.history.state, '', newHref);
    }
  }, [houseState.phase, houseState.selectedRoomId]); // eslint-disable-line

  // Reset on project switch
  useEffect(() => {
    loadedRef.current = false;
    savedSnapshotRef.current = null;
    setIsDirty(false);
  }, [projectId]);

  // Track dirty state by watching saveable fields
  const saveableKey = useMemo(() => JSON.stringify(pickSaveable(houseState)), [
    houseState.rooms, houseState.designs, houseState.furniture, houseState.lighting,
    houseState.wallSegments, houseState.freeWalls, houseState.freeRooms, houseState.autoWallOpenings, houseState.wallHeight,
  ]);

  useEffect(() => {
    if (!loadedRef.current || savedSnapshotRef.current === null) return;
    setIsDirty(saveableKey !== savedSnapshotRef.current);
  }, [saveableKey]);

  // ── Mark saved (called by child save handlers) ─────────────────────────────
  function markSaved() {
    savedSnapshotRef.current = JSON.stringify(pickSaveable(store.getState().house));
    setIsDirty(false);
  }

  // ── Block in-app navigation when dirty ────────────────────────────────────
  const blocker = useBlocker(isDirty);

  useEffect(() => {
    if (blocker.state === 'blocked') setShowModal(true);
  }, [blocker.state]);

  // ── Block tab close / browser navigate away ────────────────────────────────
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // ── Confirm-before-leaving for in-app navigation that isn't a router
  // transition — e.g. RoomEditor/FloorPlan/Builder's own "back" buttons,
  // which just dispatch a phase change directly and previously skipped the
  // unsaved-changes check entirely (useBlocker only sees real route changes,
  // so switching phases in place never tripped it). Anything wanting to
  // leave the current editor without a real navigation should route through
  // this instead of dispatching the phase change straight away.
  function confirmIfDirty(action) {
    if (!isDirty) { action(); return; }
    pendingActionRef.current = action;
    setShowModal(true);
  }

  // Resumes whatever was blocked — either a real router navigation
  // (blocker.proceed) or an in-app action queued via confirmIfDirty.
  function proceedPending() {
    if (pendingActionRef.current) {
      const action = pendingActionRef.current;
      pendingActionRef.current = null;
      action();
    } else {
      blocker.proceed?.();
    }
  }

  // ── Modal handlers ─────────────────────────────────────────────────────────
  async function handleSaveAndLeave() {
    if (!projectId) { proceedPending(); setShowModal(false); return; }
    setIsSavingLeave(true);
    try {
      const s = store.getState().house;
      const sceneData = JSON.stringify({
        version: FILE_VERSION, savedAt: new Date().toISOString(),
        phase: s.phase, rooms: s.rooms, designs: s.designs, furniture: s.furniture, lighting: s.lighting || [],
        wallSegments: s.wallSegments || [], freeWalls: s.freeWalls || [], nextFreeWallId: s.nextFreeWallId || 1,
        freeRooms: s.freeRooms || [], nextFreeRoomId: s.nextFreeRoomId || 1,
        autoWallOpenings: s.autoWallOpenings || {},
        wallHeight: s.wallHeight ?? 2.8, nextId: s.nextId,
        nextFurnitureId: s.nextFurnitureId, nextLightingId: s.nextLightingId || 1, nextOpeningId: s.nextOpeningId,
        nextWallId: s.nextWallId || 1, nextWallOpeningId: s.nextWallOpeningId || 1,
      });
      await saveProjectToWeb(projectId, sceneData);
      markSaved();
    } catch (err) {
      console.error('Save before leave failed:', err);
    } finally {
      setIsSavingLeave(false);
      proceedPending();
      setShowModal(false);
    }
  }

  function handleLeaveAnyway() {
    proceedPending();
    setShowModal(false);
  }

  function handleStay() {
    if (pendingActionRef.current) {
      pendingActionRef.current = null;
    } else {
      blocker.reset?.();
    }
    setShowModal(false);
  }

  // ── Loading / error states ─────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100vh', background: '#0F1117', color: '#a5b4fc',
        flexDirection: 'column', gap: 16, fontFamily: 'system-ui,sans-serif',
      }}>
        <svg width="36" height="36" viewBox="0 0 14 14" fill="none"
          style={{ animation: 'spin 1s linear infinite' }}>
          <rect x="1.5" y="1.5" width="11" height="11" rx="1.5" stroke="#6366F1" strokeWidth="1.3"/>
          <rect x="3.5" y="1.5" width="5"  height="4"   rx="0.5" stroke="#6366F1" strokeWidth="1.2"/>
          <rect x="3"   y="7"   width="8"  height="4.5" rx="0.8" stroke="#6366F1" strokeWidth="1.2"/>
        </svg>
        <span style={{ fontSize: 14, letterSpacing: '0.04em', opacity: 0.7 }}>Loading project…</span>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (isError) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100vh', background: '#0F1117', color: '#f87171',
        flexDirection: 'column', gap: 12, fontFamily: 'system-ui,sans-serif',
      }}>
        <span style={{ fontSize: 32 }}>⚠</span>
        <span style={{ fontSize: 14 }}>Failed to load project. Check your connection and try again.</span>
      </div>
    );
  }

  const phase = houseState.phase;

  return (
    <UnsavedChangesCtx.Provider value={{ markSaved, isDirty, changeToken: saveableKey, confirmIfDirty }}>
      <div className="app">
        {/* The name comes from the project ALREADY FETCHED here — the header
            has only the id from the route, and a project document carries the
            whole scene, so reading one again just to label a share card is the
            most expensive way to get a string in this app. */}
        <Header projectName={fetchedData?.projectName || ''} />
        <div className="app-body">
          {phase === 'build'  && <Builder />}
          {phase === 'plan'   && <FloorPlan />}
          {phase === 'design' && <RoomEditor />}
          {phase === 'view3d' && <House3DView />}
        </div>
      </div>

      {showModal && (
        <UnsavedModal
          onSave={isSavingLeave ? null : handleSaveAndLeave}
          onLeave={handleLeaveAnyway}
          onStay={handleStay}
          saving={isSavingLeave}
        />
      )}
    </UnsavedChangesCtx.Provider>
  );
}

// "/try" — the unauthenticated guest flow. No projectId, no Firestore fetch,
// no autosave/dirty-tracking/leave-confirmation (there's no cloud project to
// lose): just Redux state seeded once, either fresh or resumed from a
// previous guest session's localStorage snapshot (see guestDesign.js), and
// the same phase-based view switch AppInner uses.
function GuestApp() {
  const dispatch = useDispatch();
  const houseState = useSelector(s => s.house);
  const loadedRef = useRef(false);
  const [showWelcome, setShowWelcome] = useState(true);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    const design = loadGuestDesign() || BLANK_DESIGN;
    dispatch(loadDesign({ ...design, phase: 'build', gridSize: GRID_SIZES[DEFAULT_GRID_SIZE_CODE] }));
  }, [dispatch]);

  // Drop the guest snapshot (written by the "Save" button, see guestDesign.js)
  // if the trial is abandoned rather than turned into an account: closing the
  // tab, refreshing, or navigating to an outside URL. beforeunload covers
  // desktop browsers; pagehide is the reliable equivalent on iOS/mobile
  // Safari, which doesn't fire beforeunload consistently. (In-app navigation
  // back to the landing page is handled separately, at Header's logo click —
  // a plain unmount-cleanup here would also fire from React StrictMode's
  // dev-only double-invoke immediately after mount, which would wipe a
  // just-resumed session that never actually left the page.)
  // GuestRegisterModal's own project-creation flow already clears the
  // snapshot itself once the design is safely copied into the new cloud
  // project, so clearing it again here on that path is a harmless no-op.
  useEffect(() => {
    const handleUnload = () => clearGuestDesign();
    window.addEventListener('beforeunload', handleUnload);
    window.addEventListener('pagehide', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      window.removeEventListener('pagehide', handleUnload);
    };
  }, []);

  const phase = houseState.phase;

  return (
    <GuestModeProvider value={true}>
      <UnsavedChangesCtx.Provider value={{ markSaved: () => {}, isDirty: false, changeToken: '', confirmIfDirty: (action) => action() }}>
        <div className="app">
          <Header />
          <div className="app-body">
            {phase === 'build'  && <Builder />}
            {phase === 'plan'   && <FloorPlan />}
            {phase === 'design' && <RoomEditor />}
            {phase === 'view3d' && <House3DView />}
          </div>
        </div>
        {showWelcome && <GuestWelcomeModal onClose={() => setShowWelcome(false)} />}
      </UnsavedChangesCtx.Provider>
    </GuestModeProvider>
  );
}

// The whole app sits under one layout route so the chat overlay can be mounted
// ONCE, inside the router. Inside matters: the thread links to a designer's
// profile, and a <Link> outside a router context throws. Mounting it per page
// would also mean two copies — and two listeners — the moment two entry points
// were on screen together.
const router = createBrowserRouter([
  {
    element: (
      <ChatUIProvider>
        {/* Inside ChatUIProvider: a toast opens the conversation it belongs to,
            so it needs openMessages. One listener for the whole app lives here
            — see NotificationProvider. */}
        <NotificationProvider>
        {/* ONE basket subscription for the app. Every product's add button and
            the count in the profile header read from it, so paging a lightbox
            costs no reads at all — see BasketContext. */}
        <BasketProvider>
          {/* Pages register their loaders here, so pulling to refresh re-fetches
              data instead of reloading the browser. Must wrap the Outlet: that
              is where the pages doing the registering live. */}
          <RefreshProvider>
            {/* Wrapper exists so pull-to-refresh can nudge the PAGE down with
                the finger. It carries no styles of its own and no transform at
                rest — a permanent transform here would become the containing
                block for every `position: fixed` descendant in the app. */}
            <div id="ptr-shift">
              <Outlet />
            </div>
            <MessageToasts />
            {/* Asks once, on the first open of the installed app. Renders
                nothing in a browser tab or once it has been answered. */}
            <NotificationPrimer />
            {/* Touch-only, and never on the editor routes — see PullToRefresh. */}
            <PullToRefreshHost />
            {/* Phones only, and outside #ptr-shift so it stays put while the
                page slides down under a pull. */}
            <MobileTabBar />
            {/* Bottom-right on every public page; hides itself on the editor
                routes and /admin — see SupportWidget. */}
            <SupportWidget />
          </RefreshProvider>
        </BasketProvider>
        </NotificationProvider>
      </ChatUIProvider>
    ),
    children: [
  { path: '/',                element: <Landing /> },
  { path: '/try',             element: <GuestApp /> },
  { path: '/login',           element: <Login /> },
  { path: '/register',        element: <Register /> },
  { path: '/forgot-password', element: <ForgotPassword /> },
  // Public on purpose: a privacy policy behind a login is not a privacy policy.
  { path: '/about',           element: <About /> },
  { path: '/privacy',         element: <Privacy /> },
  // Public: someone deciding whether to sign up needs to see what it costs.
  { path: '/plans',           element: <Plans /> },
  // The customer/designer question a Google sign-up never got asked. Protected
  // — it is a step in creating an account, so there is always a user by the
  // time anyone can reach it. ProtectedRoute lets this one path through when
  // the flag is set, or it would redirect the page to itself.
  { path: '/account-type',    element: <ProtectedRoute><AccountType /></ProtectedRoute> },
  { path: '/dashboard',       element: <ProtectedRoute><Dashboard /></ProtectedRoute> },
  { path: '/profile',         element: <ProtectedRoute><Profile /></ProtectedRoute> },
  { path: '/settings',        element: <ProtectedRoute><Settings /></ProtectedRoute> },
  { path: '/workspace',       element: <ProtectedRoute><Workspace /></ProtectedRoute> },
  { path: '/app/:projectId',  element: <ProtectedRoute><AppInner /></ProtectedRoute> },
  // Public, unauthenticated, read-only design view (no ProtectedRoute).
  { path: '/share/:userId/:projectId', element: <SharedProject /> },
  // Public, unauthenticated: browsing designers must not require an account.
  { path: '/search',          element: <SearchPage /> },
  // ── THE DIRECTORY AND THE MARKETPLACE, AT THEIR OWN ADDRESSES ─────────────
  // The homepage carries only a small preview of each and links here for the
  // rest, so the full search, filters, sorting and paging need URLs that say
  // what they are. All three are the same component — see SearchPage — because
  // the two differ only in which rows they filter and which controls narrow
  // them, and both of those already branch on the mode inside it.
  //
  // `/shop` sits above `/shop/:uid` deliberately: a bare /shop is the
  // marketplace, /shop/<id> is one shop's profile.
  //
  // `locked` — each is a page about ONE kind of thing, so neither shows the
  // three-way mode switcher. /search still does; it is the general search, and
  // the only route the shops directory is reachable from.
  { path: '/designers',       element: <SearchPage defaultMode={USER_TYPES.DESIGNER} locked /> },
  { path: '/shop',            element: <SearchPage defaultMode="product" locked /> },
  { path: '/designer/:uid',   element: <DesignerProfile /> },
  // Same page, other role — see DesignerProfile. A separate PATH rather than a
  // shared /profile/:uid, so a link says what it leads to and the page never has
  // to read the account before it knows which directory to query.
  { path: '/shop/:uid',       element: <DesignerProfile role={USER_TYPES.SHOP} /> },
  // ONE LISTING, at its own address. Public like the two above: a product link
  // has to open for whoever it was sent to. Opening one used to navigate to the
  // shop with `?photo=` on the URL, which left the reader on a page they never
  // asked for once they closed the picture — see ProductPage.
  { path: '/product/:id',     element: <ProductPage /> },
  { path: '/my-designers',    element: <ProtectedRoute><MyDesigners /></ProtectedRoute> },
  { path: '/my-liked',        element: <ProtectedRoute><MyLiked /></ProtectedRoute> },
  // Yours alone, so it needs an account — the rows live under users/{uid}.
  { path: '/basket',          element: <ProtectedRoute><Basket /></ProtectedRoute> },
  // ADMIN ONLY, gated on the `admin` custom claim rather than on being signed
  // in — see AdminRoute. The guard is a convenience: the data behind this page
  // is refused by firestore.rules and by the callables to anyone without the
  // claim, so reaching the route by hand shows an empty shell, not the numbers.
  { path: '/admin',           element: <AdminRoute><AdminDashboard /></AdminRoute> },

  { path: '*',                element: <NotFound /> },
    ],
  },
]);

export default function App() {
  // One listener for the whole app, stamping the scroll direction on <html>.
  // The sticky header and back bar hide and reveal from CSS off that attribute.
  useScrollDirection();

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <LangProvider>
          <AuthProvider>
            <RouterProvider router={router} />
          </AuthProvider>
        </LangProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
